// Libraries
import I18n from 'i18next'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import React, { Component, Fragment } from 'react'
import { bindActionCreators } from 'redux'
import { message, Modal } from 'antd'
import { get, includes, defaultTo, isNil } from 'lodash'

// Ressources
import { actions as habilitationActions } from 'Resources/HabilitationResource'

// Common components
import { ManageModal } from 'Components'

// Specs
import { ModalSpecs } from 'Views/Habilitations/Specs'

// Écrans
import { List } from './Screens'
import { formatOrderedData } from 'Helpers/index'

const { confirm } = Modal

class HabilitationsConstructor extends Component {
  static propTypes = {
    // Actions d'API
    actions: PropTypes.object,

    // Données de la vue
    habilitation: PropTypes.object,
    habilitations: PropTypes.array,

    // Témoin d'activité API
    isGathering: PropTypes.bool,
    isUpdating: PropTypes.bool,
    isCreating: PropTypes.bool,

    // Navigation interne
    navigation: PropTypes.shape({
      navigate: PropTypes.func.isRequired,
      getParam: PropTypes.func.isRequired,
      setParams: PropTypes.func.isRequired
    })
  }

  static defaultProps = {
    // Actions d'API
    actions: {},

    // Données de la vue
    habilitation: {},
    habilitations: [],

    // Témoin d'activité API
    isGathering: false,
    isUpdating: false,
    isCreating: false
  }

  constructor(props) {
    super(props)

    this.state = {
      openedModal: null
    }
  }

  componentDidMount = () => {
    // Récupération des données des éléments de la vue
    this.fetchHabilitationsData()

    if (this.getContext() === 'single') {
      this.getHabilitationData().then(this.askUpdateHabilitation)
    }
  }

  /**
   * Récupération du contexte actuel du constructeur de page
   */
  getContext = () => {
    return isNil(this.props.navigation.getParam('id')) ? 'list' : 'single'
  }

  /**
   * Récupération des données des éléments de la vue
   */
  fetchHabilitationsData = () => {
    this.props.actions.fetchHabilitations().catch(() => {
      message.error(I18n.t('api.errors.habilitation.fetch'))
    })
  }

  /**
   * Récupération des données détaillées d'un élément de la vue
   */
  getHabilitationData = (habilitation = this.props.navigation.getParam('id')) =>
    new Promise((resolve, reject) => {
      this.props.actions
        .getHabilitation(habilitation)
        .then(response => {
          // Détails récupérés
          resolve(get(response, 'body'), response)
        })
        .catch(error => {
          // Erreur lors du refresh
          reject(error)
          message.error(I18n.t('api.errors.habilitation.get'))
        })
    })

  /**
   * Actions groupées:
   * Archivage de plusieurs lignes du tableau
   */
  archiveManyHabilitation = (habilitation, clearTableSelections) => {
    this.props.actions
      .archiveManyHabilitation(habilitation)
      .then(() => {
        clearTableSelections()
        message.success(I18n.t('api.success.habilitation.bulk.archive'))
      })
      .catch(() => {
        message.error(I18n.t('api.errors.habilitation.bulk.archive'))
      })
  }

  /**
   * Archivage d'une ligne du tableau
   */
  archiveHabilitation = habilitation => {
    this.props.actions
      .archiveHabilitation(habilitation)
      .then(() => {
        this.safelyCloseModal()
        this.props.navigation.setParams({ id: null })
        message.success(I18n.t('api.success.habilitation.archive'))
      })
      .catch(() => {
        message.error(I18n.t('api.errors.habilitation.archive'))
      })
  }

  /**
   * Actions groupées:
   * Suppression de plusieurs lignes du tableau
   */
  deleteManyHabilitation = (habilitation, clearTableSelections) => {
    this.props.actions
      .archiveHabilitations(habilitation)
      .then(() => {
        clearTableSelections()
        message.success(I18n.t('api.success.habilitation.bulk.delete'))
      })
      .catch(() => {
        message.error(I18n.t('api.errors.habilitation.bulk.delete'))
      })
  }

  /**
   * Suppression d'une ligne du tableau
   */
  deleteHabilitation = habilitation => {
    this.props.actions
      .archiveHabilitation(habilitation)
      .then(() => {
        this.safelyCloseModal()
        this.props.navigation.setParams({ id: null })
        message.success(I18n.t('api.success.habilitation.delete'))
      })
      .catch(() => {
        message.error(I18n.t('api.errors.habilitation.delete'))
      })
  }

  /**
   * Mise à jour d'une habilitation
   */
  updateHabilitation = (updatedHabitation, originalHabilitation) =>
    new Promise((resolve, reject) => {
      this.props.actions
        .updateHabilitation({
          id: get(originalHabilitation, 'id'),
          ...updatedHabitation
        })
        .then(response => {
          this.safelyCloseModal()
          resolve(get(response, 'body'), response)
          message.success(I18n.t('api.success.habilitation.update'))
        })
        .catch(error => {
          reject(error)
          message.error(I18n.t('api.errors.habilitation.update'))
        })
    })

  /**
   * Création d'une habilitation
   */
  createHabilitation = createdHabilitation =>
    new Promise((resolve, reject) => {
      this.props.actions
        .createHabilitation(createdHabilitation)
        .then(response => {
          this.safelyCloseModal()
          resolve(get(response, 'body'), response)
          message.success(I18n.t('api.success.habilitation.create'))
        })
        .catch(error => {
          reject(error)
          message.error(I18n.t('api.errors.habilitation.create'))
        })
    })

  /**
   * Actions groupées:
   * Demande d'archivage de plusieurs lignes du tableau
   */
  askArchiveManyHabilitations = (...params) => {
    confirm({
      title: I18n.t('pages.habilitations.modal.archive.multiple.title'),
      content: I18n.t('pages.habilitations.modal.archive.multiple.content'),
      okText: I18n.t('pages.habilitations.modal.archive.multiple.okText'),
      okType: 'danger',
      cancelText: I18n.t(
        'pages.habilitations.modal.archive.multiple.cancelText'
      ),
      onOk: () => {
        this.archiveHabilitation(...params)
      },
      maskClosable: true
    })
  }

  /**
   * Demande d'archivage d'une ligne du tableau
   */
  askArchiveHabilitation = () => {
    const { habilitation } = this.props

    confirm({
      title: I18n.t('pages.habilitations.modal.archive.single.title'),
      content: I18n.t('pages.habilitations.modal.archive.single.content'),
      okText: I18n.t('pages.habilitations.modal.archive.single.okText'),
      okType: 'danger',
      cancelText: I18n.t('pages.habilitations.modal.archive.single.cancelText'),
      onOk: () => {
        this.archiveHabilitation(habilitation)
      },
      maskClosable: true
    })
  }

  /**
   * Actions groupées:
   * Demande d'archivage de plusieurs lignes du tableau
   */
  askDeleteManyHabilitations = (...params) => {
    confirm({
      title: I18n.t('pages.habilitations.modal.delete.multiple.title'),
      content: I18n.t('pages.habilitations.modal.delete.multiple.content'),
      okText: I18n.t('pages.habilitations.modal.delete.multiple.okText'),
      okType: 'danger',
      cancelText: I18n.t(
        'pages.habilitations.modal.delete.multiple.cancelText'
      ),
      onOk: () => {
        this.deleteManyHabilitation(...params)
      },
      maskClosable: true
    })
  }

  /**
   * Demande d'archivage d'une ligne du tableau
   */
  askDeleteHabilitation = () => {
    const { habilitation } = this.props

    confirm({
      title: I18n.t('pages.habilitations.modal.delete.single.title'),
      content: I18n.t('pages.habilitations.modal.delete.single.content'),
      okText: I18n.t('pages.habilitations.modal.delete.single.okText'),
      okType: 'danger',
      cancelText: I18n.t('pages.habilitations.modal.delete.single.cancelText'),
      onOk: () => {
        this.deleteHabilitation(habilitation)
      },
      maskClosable: true
    })
  }

  /**
   * Demande de modification d'une ligne du tableau
   */
  askUpdateHabilitation = () => {
    this.setState({ openedModal: 'updateHabilitation' })
  }

  /**
   * Demande de création d'une ligne du tableau
   */
  askCreateHabilitation = () => {
    this.setState({ openedModal: 'createHabilitation' })
  }

  /**
   * Fermeture de toutes les modales
   */
  safelyCloseModal = () => {
    const { openedModal } = this.state

    if (openedModal === 'updateHabilitation') {
      this.setState({ openedModal: 'transition' }, () => {
        setTimeout(
          this.setState({ openedModal: null }, () => {
            this.props.navigation.setParams({ id: null })
          }),
          200
        )
      })
    } else {
      this.setState({ openedModal: null })
    }
  }

  /**
   * Callback:
   * Création d'une ligne de tableau réussie
   */
  handleRowCreated = () => {
    this.safelyCloseModal()
  }

  /**
   * Callback:
   * Modification d'une ligne de tableau réussie
   */
  handleRowUpdated = () => {
    this.safelyCloseModal()
  }

  /**
   * Clic sur une ligne du tableau
   */
  handleRowClick = habilitation => {
    // Navigation vers la page détaillée
    this.props.navigation.setParams({ id: get(habilitation, 'id') })

    this.getHabilitationData(habilitation)
      .then(() =>
        // Ouverture de la modale
        this.askUpdateHabilitation()
      )
      .catch(() => {
        this.props.navigation.setParams({ id: null })
      })

    // Ouverture du drawer
    // this.setState({ drawerOpened: true })

    // Navigation vers la vue détaillée
    // this.props.navigation.navigate('Habilitation', { id: get(habilitation, 'id') })
  }

  handleDraggedRowReleased = orderedData => {
    const formattedOrderedData = formatOrderedData(orderedData)

    this.props.actions
      .orderHabilitation(formattedOrderedData)
      .then(() => message.success(I18n.t('api.success.habilitation.order')))
      .catch(() => message.error(I18n.t('api.errors.habilitation.order')))
  }
  /**
   * Formatage des lignes de la modale de modification / création
   */
  formatModalRows = rows => {
    return rows
  }

  render() {
    // Récupération des éléments des props
    let {
      isGathering,
      isUpdating,
      isCreating,
      habilitations,
      habilitation
    } = this.props

    // Récupération des éléments du state
    const { openedModal } = this.state

    // Chargement
    const loading = isGathering || isUpdating || isCreating

    // Création de la liste des fonctions constructeur exposés au écrans
    const constructorFunctions = {
      // Clic sur un ligne de tableau
      handleRowClick: this.handleRowClick,

      // Gestion de la modale
      safelyCloseModal: this.safelyCloseModal,

      // Récupération des données détaillées d'une ligne du tableau
      getHabilitationData: this.getHabilitationData,

      // Création d'une ligne de tableau
      handleRowCreated: this.handleRowCreated,
      askCreateHabilitation: this.askCreateHabilitation,

      // Modification d'une ou plusieurs ligne(s) de tableau
      handleRowUpdated: this.handleRowUpdated,
      askUpdateHabilitation: this.askUpdateHabilitation,

      handleDraggedRowReleased: this.handleDraggedRowReleased,

      // Archivage d'une ou plusieurs ligne(s) du tableau
      askArchiveHabilitation: this.askArchiveHabilitation,
      askArchiveManyHabilitations: this.askArchiveManyHabilitations,

      // Suppression d'une ou plusieurs ligne(s) du tableau
      askDeleteHabilitation: this.askDeleteHabilitation,
      askDeleteManyHabilitations: this.askDeleteManyHabilitations
    }

    // Ajout des actions sur la modale
    ModalSpecs.actions.delete.onClick = this.askDeleteHabilitation

    // Ajout des lignes au formulaire
    // formLayout = merge(formLayout, manageModal)

    return (
      <Fragment>
        {/* Rendu de l'écran approprié au contexte du constructeur */}
        <List
          loading={loading}
          constructorFunctions={constructorFunctions}
          constructorState={this.state}
          dataList={habilitations}
        />

        {/* Ouverture de la modale de création / modification */}
        <ManageModal
          width={800}
          loading={loading}
          onCancel={this.safelyCloseModal}
          onOk={this.handleRowCreated}
          onChange={
            openedModal === 'updateHabilitation'
              ? this.updateHabilitation
              : this.createHabilitation
          }
          visible={includes(
            ['createHabilitation', 'updateHabilitation'],
            openedModal
          )}
          data={
            includes(['transition', 'updateHabilitation'], openedModal)
              ? habilitation
              : null
          }
          {...ModalSpecs}
        />
      </Fragment>
    )
  }
}

const mapStateToProps = state => {
  const defaultProps = get(HabilitationsConstructor, 'defaultProps', {})

  return {
    habilitations: defaultTo(
      get(state, 'habilitation.items'),
      defaultProps.habilitations
    ),
    habilitation: defaultTo(
      get(state, 'habilitation.item'),
      defaultProps.habilitation
    ),
    isGathering:
      defaultTo(
        get(state, 'habilitation.isFetching'),
        defaultProps.isGathering
      ) ||
      defaultTo(
        get(state, 'habilitation.isFetchingItem'),
        defaultProps.isGathering
      ),
    isUpdating: defaultTo(
      get(state, 'habilitation.isUpdating'),
      defaultProps.isUpdating
    )
  }
}

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ ...habilitationActions }, dispatch)
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(HabilitationsConstructor)
